import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Swords, User, Zap, Info, History, ChevronDown, ChevronUp,
  Trophy, Minus, MessageSquare, Scale, BookOpen, Users, Check,
  ChevronLeft, ChevronRight,
} from "lucide-react";
import type { Bot, Scenario, Duel, NegotiationCase, RolePair } from "@shared/schema";

/** Публичные внешние боты (id без префикса ext:). */
interface ArenaExternalBot {
  id: string;
  name: string;
  avatar: string;
  rating: number;
}

/* ─── Stepper for case → role pair selection ─── */

function CaseRoleStepper({
  selectedCaseId,
  onSelectCase,
  selectedRolePairId,
  onSelectRolePair,
  cases,
  mode,
}: {
  selectedCaseId: string;
  onSelectCase: (id: string) => void;
  selectedRolePairId: string;
  onSelectRolePair: (id: string) => void;
  cases: NegotiationCase[];
  mode: "bot" | "human";
}) {
  const selectedCase = cases.find(c => c.id === selectedCaseId);
  const selectedRolePair = selectedCase?.rolePairs.find((rp: RolePair) => rp.id === selectedRolePairId);
  const step = !selectedCaseId ? 1 : !selectedRolePairId ? 2 : 3;

  return (
    <div className="space-y-4">
      {/* Step 1: Case */}
      <Card className={`p-5 space-y-4 transition-colors ${step === 1 ? "border-primary/40" : ""}`}>
        <div className="flex items-center gap-3">
          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
            selectedCaseId ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
          }`}>
            {selectedCaseId ? <Check className="w-3.5 h-3.5" /> : "1"}
          </span>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Кейс</h2>
        </div>

        <Select value={selectedCaseId} onValueChange={onSelectCase}>
          <SelectTrigger data-testid={`select-case-${mode}`}>
            <SelectValue placeholder="Выберите кейс переговоров" />
          </SelectTrigger>
          <SelectContent>
            {cases.map(c => (
              <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {selectedCase && (
          <div className="bg-muted/30 rounded-md p-4 space-y-3">
            <p className="text-sm leading-relaxed">{selectedCase.description}</p>
            <div className="flex items-center gap-3 flex-wrap">
              <Badge variant="outline" className="text-xs gap-1">
                <BookOpen className="w-3 h-3" />
                {selectedCase.author}
              </Badge>
              <Badge variant="outline" className="text-xs gap-1">
                <Users className="w-3 h-3" />
                {selectedCase.rolePairs.length} {selectedCase.rolePairs.length === 1 ? "пара" : selectedCase.rolePairs.length < 5 ? "пары" : "пар"} ролей
              </Badge>
              <Badge variant="outline" className="text-xs">
                {selectedCase.difficulty === "hard" ? "Сложный" : selectedCase.difficulty === "easy" ? "Простой" : "Средний"}
              </Badge>
              <Badge variant="outline" className="text-xs">
                {selectedCase.maxRounds} раундов
              </Badge>
            </div>
          </div>
        )}
      </Card>

      {/* Step 2: Role Pair */}
      <Card className={`p-5 space-y-4 transition-all ${
        !selectedCaseId ? "opacity-40 pointer-events-none" : step === 2 ? "border-primary/40" : ""
      }`}>
        <div className="flex items-center gap-3">
          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
            selectedRolePairId ? "bg-primary text-primary-foreground" : selectedCaseId ? "bg-muted text-muted-foreground" : "bg-muted/50 text-muted-foreground/50"
          }`}>
            {selectedRolePairId ? <Check className="w-3.5 h-3.5" /> : "2"}
          </span>
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Ролевая пара</h2>
        </div>

        {selectedCase && (
          <div className="space-y-2">
            {selectedCase.rolePairs.map((rp: RolePair) => {
              const isSelected = selectedRolePairId === rp.id;
              return (
                <button
                  key={rp.id}
                  className={`w-full text-left rounded-lg p-4 transition-all border ${
                    isSelected
                      ? "border-primary/50 bg-primary/5"
                      : "border-border bg-muted/20 hover:border-primary/30 hover:bg-muted/40"
                  }`}
                  onClick={() => onSelectRolePair(rp.id)}
                  data-testid={`select-role-pair-${rp.id}`}
                >
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className={`text-xs font-semibold ${mode === "human" ? "text-primary" : "text-red-400"}`}>
                        {mode === "human" ? "Ваша роль (A)" : "Роль A"}
                      </span>
                      <p className="text-sm mt-0.5">{rp.roleA}</p>
                      <p className="text-xs text-muted-foreground mt-1">{rp.goalA}</p>
                    </div>
                    <div>
                      <span className="text-xs font-semibold text-blue-400">
                        {mode === "human" ? "Оппонент (B)" : "Роль B"}
                      </span>
                      <p className="text-sm mt-0.5">{rp.roleB}</p>
                      <p className="text-xs text-muted-foreground mt-1">{rp.goalB}</p>
                    </div>
                  </div>
                  {isSelected && (
                    <div className="flex items-center gap-1 mt-2">
                      <Check className="w-3.5 h-3.5 text-primary" />
                      <span className="text-xs text-primary font-medium">Выбрано</span>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}

/* ─── Duel History Item ─── */

function DuelHistoryItem({
  duel,
  bots,
  scenarios,
  externalBots = [],
}: {
  duel: Duel;
  bots: Bot[];
  scenarios: Scenario[];
  externalBots?: ArenaExternalBot[];
}) {
  const [expanded, setExpanded] = useState(false);

  const botA =
    bots.find((b) => b.id === duel.botAId) ||
    (() => {
      const e = externalBots.find((x) => `ext:${x.id}` === duel.botAId);
      return e
        ? ({ id: duel.botAId, name: e.name, avatar: e.avatar, style: "", description: "" } as unknown as Bot)
        : undefined;
    })();
  const botB = duel.botBId
    ? bots.find((b) => b.id === duel.botBId) ||
      (() => {
        const e = externalBots.find((x) => `ext:${x.id}` === duel.botBId);
        return e
          ? ({ id: duel.botBId!, name: e.name, avatar: e.avatar, style: "", description: "" } as unknown as Bot)
          : undefined;
      })()
    : null;
  const scenario = scenarios.find((s) => s.id === duel.scenarioId);
  const rounds = duel.rounds || [];

  const isHumanMode = duel.mode === "human_vs_bot";
  const nameA = isHumanMode ? "Вы" : botA?.name || "Бот A";
  const nameB = botB?.name || botA?.name || "Бот B";
  const avatarA = isHumanMode ? "👤" : botA?.avatar || "🤖";
  const avatarB = isHumanMode ? botA?.avatar || "🤖" : botB?.avatar || "🤖";

  const winnerLabel =
    duel.winnerId === "draw"
      ? "Ничья"
      : duel.winnerId === "human"
        ? "Победа: Вы"
        : duel.winnerId === duel.botAId
          ? `Победа: ${botA?.name || "Бот A"}`
          : `Победа: ${botB?.name || botA?.name || "Бот B"}`;

  const winnerColor =
    duel.winnerId === "draw"
      ? "text-muted-foreground"
      : "text-primary";

  const statusBadge =
    duel.status === "completed" ? (
      <Badge variant="outline" className="text-xs border-primary/30 text-primary">Завершён</Badge>
    ) : duel.status === "active" ? (
      <Badge variant="outline" className="text-xs border-yellow-500/30 text-yellow-500">В процессе</Badge>
    ) : (
      <Badge variant="outline" className="text-xs">Ожидает</Badge>
    );

  return (
    <Card className="overflow-hidden transition-colors hover:border-primary/30" data-testid={`duel-history-item-${duel.id}`}>
      <button className="w-full p-4 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 text-left" onClick={() => setExpanded(!expanded)} data-testid={`button-expand-duel-${duel.id}`}>
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span className="text-lg shrink-0">{avatarA}</span>
          <span className="text-sm font-medium truncate">{nameA}</span>
          <Swords className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
          <span className="text-lg shrink-0">{avatarB}</span>
          <span className="text-sm font-medium truncate">{nameB}</span>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {duel.status === "completed" && (
            <span className="text-xs font-mono tabular-nums text-muted-foreground">
              {(duel as any).pointsA !== undefined ? `${(duel as any).pointsA} : ${(duel as any).pointsB} очк.` : `${duel.scoreA} : ${duel.scoreB}`}
            </span>
          )}
          {statusBadge}
          {expanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-border px-4 pb-4 space-y-4">
          <div className="pt-4 flex flex-wrap items-center gap-3 text-sm">
            {scenario && <Badge variant="secondary" className="text-xs">{scenario.title}</Badge>}
            <Badge variant="secondary" className="text-xs">{duel.mode === "human_vs_bot" ? "Я vs Бот" : "Бот vs Бот"}</Badge>
            {duel.status === "completed" && (
              <span className={`text-sm font-medium flex items-center gap-1 ${winnerColor}`}>
                {duel.winnerId === "draw" ? <Minus className="w-3.5 h-3.5" /> : <Trophy className="w-3.5 h-3.5" />}
                {winnerLabel}
              </span>
            )}
          </div>

          {duel.status === "completed" && (duel.scoreA > 0 || duel.scoreB > 0) && (
            <div className="space-y-1.5">
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{avatarA} {nameA}: {duel.scoreA}</span>
                <span>{avatarB} {nameB}: {duel.scoreB}</span>
              </div>
              <div className="flex h-2 rounded-full overflow-hidden bg-muted">
                <div className="bg-red-500/70 transition-all" style={{ width: `${(duel.scoreA / (duel.scoreA + duel.scoreB)) * 100}%` }} />
                <div className="bg-blue-500/70 transition-all" style={{ width: `${(duel.scoreB / (duel.scoreA + duel.scoreB)) * 100}%` }} />
              </div>
            </div>
          )}

          {rounds.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                <MessageSquare className="w-3.5 h-3.5" /> Раунды ({rounds.length})
              </h3>
              <div className="space-y-2">
                {rounds.map((round) => (
                  <div key={round.round} className="bg-muted/30 rounded-lg p-3 space-y-2" data-testid={`round-${duel.id}-${round.round}`}>
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Раунд {round.round}</span>
                    <div className="space-y-2">
                      <div className="flex flex-col sm:flex-row gap-1 sm:gap-2">
                        <span className="text-red-400 shrink-0 text-xs font-medium">{avatarA} {nameA}:</span>
                        <p className="text-sm leading-relaxed">{round.speakerA}</p>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-1 sm:gap-2">
                        <span className="text-blue-400 shrink-0 text-xs font-medium">{avatarB} {nameB}:</span>
                        <p className="text-sm leading-relaxed">{round.speakerB}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Judge Verdicts (vertical list) */}
          {(duel as any).judgeVerdicts && (duel as any).judgeVerdicts.length > 0 ? (
            <div className="bg-primary/5 border border-primary/10 rounded-lg p-3">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-primary flex items-center gap-1.5 mb-3">
                <Scale className="w-3.5 h-3.5" /> Вердикты судей ({(duel as any).pointsA || 0} : {(duel as any).pointsB || 0})
              </h3>
              <div className="flex flex-col gap-2">
                {(duel as any).judgeVerdicts.map((v: any, vi: number) => (
                  <div key={vi} className="p-2.5 rounded-md bg-background/50 border border-border/30 space-y-1">
                    <div className="flex items-center gap-2 text-xs">
                      <span className="font-medium">⚖️ {v.judgeName}</span>
                      <span className="text-muted-foreground">→</span>
                      <span className={`font-medium ${v.votedFor === duel.botAId ? "text-red-400" : "text-blue-400"}`}>
                        {v.votedFor === duel.botAId ? nameA : nameB} (+1)
                      </span>
                    </div>
                    {v.comment && <p className="text-xs text-muted-foreground leading-relaxed">{v.comment}</p>}
                  </div>
                ))}
              </div>
            </div>
          ) : duel.judgeNotes ? (
            <div className="bg-primary/5 border border-primary/10 rounded-lg p-3">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-primary flex items-center gap-1.5 mb-2">
                <Scale className="w-3.5 h-3.5" /> Вердикт судьи
              </h3>
              <p className="text-sm leading-relaxed text-muted-foreground">{duel.judgeNotes}</p>
            </div>
          ) : null}

          {/* Trainer Comments */}
          {(duel as any).trainerComments && (duel as any).trainerComments.length > 0 && (
            <div className="bg-green-500/5 border border-green-500/10 rounded-lg p-3">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-green-400/80 flex items-center gap-1.5 mb-3">
                🎓 Тренерские комментарии
              </h3>
              <div className="flex flex-col gap-2">
                {(duel as any).trainerComments.map((tc: any, ti: number) => (
                  <div key={ti} className="p-2.5 rounded-md bg-background/50 border border-green-500/10 space-y-1.5">
                    <div className="text-xs font-medium">🎓 {tc.trainerName}</div>
                    {tc.feedbackA && (
                      <div className="text-xs">
                        <span className="font-medium text-red-400">{nameA}:</span>
                        <span className="text-muted-foreground ml-1 leading-relaxed">{tc.feedbackA}</span>
                      </div>
                    )}
                    {tc.feedbackB && (
                      <div className="text-xs">
                        <span className="font-medium text-blue-400">{nameB}:</span>
                        <span className="text-muted-foreground ml-1 leading-relaxed">{tc.feedbackB}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

/* ─── Arena Page ─── */

export default function ArenaPage() {
  const [, navigate] = useLocation();

  // Bot vs Bot state
  const [selectedCaseBot, setSelectedCaseBot] = useState("");
  const [selectedRolePairBot, setSelectedRolePairBot] = useState("");
  const [selectedBotA, setSelectedBotA] = useState("");
  const [selectedBotB, setSelectedBotB] = useState("");
  const [selectedJudges, setSelectedJudges] = useState<string[]>([]);
  const [duelMode, setDuelMode] = useState<"express" | "classic">("express");
  const [historyPage, setHistoryPage] = useState(1);
  const HISTORY_PER_PAGE = 10;

  // Human vs Bot state
  const [selectedCaseHuman, setSelectedCaseHuman] = useState("");
  const [selectedRolePairHuman, setSelectedRolePairHuman] = useState("");
  const [selectedBotHuman, setSelectedBotHuman] = useState("");
  const [isLaunching, setIsLaunching] = useState(false);

  const { data: bots = [] } = useQuery<Bot[]>({ queryKey: ["/api/bots"] });
  const { data: externalBots = [] } = useQuery<ArenaExternalBot[]>({ queryKey: ["/api/external-bots/all"] });
  const { data: cases = [] } = useQuery<NegotiationCase[]>({ queryKey: ["/api/cases"] });
  const { data: scenarios = [] } = useQuery<Scenario[]>({ queryKey: ["/api/scenarios"] });
  const { data: duels = [], isLoading: duelsLoading } = useQuery<Duel[]>({
    queryKey: ["/api/duels"],
    staleTime: 0,
    refetchOnMount: "always",
    refetchInterval: (query) => {
      const currentDuels = (query.state.data as Duel[] | undefined) || [];
      const hasActive = currentDuels.some((d) => d.status === "active");
      return hasActive ? 2000 : false;
    },
  });

  const handleCaseSelectBot = (caseId: string) => {
    setSelectedCaseBot(caseId);
    setSelectedRolePairBot("");
  };

  const handleCaseSelectHuman = (caseId: string) => {
    setSelectedCaseHuman(caseId);
    setSelectedRolePairHuman("");
  };

  // Create scenario from case+pair, then navigate to duel
  const handleBotVsBot = async () => {
    if (!selectedCaseBot || !selectedRolePairBot || !selectedBotA || !selectedBotB) return;
    try {
      const res = await apiRequest("POST", "/api/scenarios/from-case", {
        caseId: selectedCaseBot,
        rolePairId: selectedRolePairBot,
      });
      const scenario = await res.json();
      const judgeParam = selectedJudges.filter(j => j && j !== "__default__").join(",");
      navigate(`/duel/new?scenario=${scenario.id}&botA=${selectedBotA}&botB=${selectedBotB}&judges=${judgeParam}&mode=${duelMode}`);
    } catch {}
  };

  const handleHumanVsBot = async () => {
    if (!selectedCaseHuman || !selectedRolePairHuman || !selectedBotHuman) return;
    setIsLaunching(true);
    try {
      const scenRes = await apiRequest("POST", "/api/scenarios/from-case", {
        caseId: selectedCaseHuman,
        rolePairId: selectedRolePairHuman,
      });
      const scenario = await scenRes.json();
      const duelRes = await apiRequest("POST", "/api/duels/human-vs-bot", {
        scenarioId: scenario.id,
        botAId: selectedBotHuman,
      });
      const duel = await duelRes.json();
      navigate(`/human-duel/${duel.id}`);
    } catch {
      setIsLaunching(false);
    }
  };

  const botVsBotReady = selectedCaseBot && selectedRolePairBot && selectedBotA && selectedBotB && selectedBotA !== selectedBotB;
  const humanVsBotReady = selectedCaseHuman && selectedRolePairHuman && selectedBotHuman;

  const sortedDuels = [...duels].sort((a, b) => {
    if (a.status === "completed" && b.status !== "completed") return -1;
    if (a.status !== "completed" && b.status === "completed") return 1;
    return (b.rounds?.length || 0) - (a.rounds?.length || 0);
  });

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-xl font-semibold mb-2" data-testid="text-arena-title">
          Арена переговорных поединков
        </h1>
        <p className="text-muted-foreground text-sm max-w-2xl">
          Выберите кейс переговоров, ролевую пару и участников. Судья оценит результат.
        </p>
      </div>

      <Tabs defaultValue="bot-vs-bot" className="space-y-6">
        <TabsList className="grid w-full max-w-lg grid-cols-3">
          <TabsTrigger value="bot-vs-bot" className="gap-2" data-testid="tab-bot-vs-bot">
            <Swords className="w-4 h-4" /> Бот vs Бот
          </TabsTrigger>
          <TabsTrigger value="human-vs-bot" className="gap-2" data-testid="tab-human-vs-bot">
            <User className="w-4 h-4" /> Я vs Бот
          </TabsTrigger>
          <TabsTrigger value="history" className="gap-2" data-testid="tab-history">
            <History className="w-4 h-4" /> История
          </TabsTrigger>
        </TabsList>

        {/* ─── Bot vs Bot ─── */}
        <TabsContent value="bot-vs-bot" className="space-y-4">
          <CaseRoleStepper
            selectedCaseId={selectedCaseBot}
            onSelectCase={handleCaseSelectBot}
            selectedRolePairId={selectedRolePairBot}
            onSelectRolePair={setSelectedRolePairBot}
            cases={cases}
            mode="bot"
          />

          {/* Step 3: Bots */}
          <Card className={`p-5 space-y-4 transition-all ${
            !selectedRolePairBot ? "opacity-40 pointer-events-none" : ""
          }`}>
            <div className="flex items-center gap-3">
              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                selectedBotA && selectedBotB ? "bg-primary text-primary-foreground" : selectedRolePairBot ? "bg-muted text-muted-foreground" : "bg-muted/50 text-muted-foreground/50"
              }`}>
                {selectedBotA && selectedBotB ? <Check className="w-3.5 h-3.5" /> : "3"}
              </span>
              <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Участники</h2>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-3 p-4 rounded-lg border border-red-500/20 bg-red-500/5">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-red-500"></span>
                  Бот A (Красный угол)
                </h3>
                <Select value={selectedBotA} onValueChange={setSelectedBotA}>
                  <SelectTrigger data-testid="select-bot-a">
                    <SelectValue placeholder="Выберите бота" />
                  </SelectTrigger>
                  <SelectContent>
                    {bots.map(b => (
                      <SelectItem key={b.id} value={b.id}>
                        {b.avatar} {b.name} ({b.rating} ELO)
                      </SelectItem>
                    ))}
                    {externalBots.length > 0 && (
                      <>
                        <div className="px-2 py-1.5 text-[10px] font-semibold text-muted-foreground uppercase">Внешние</div>
                        {externalBots.map(b => (
                          <SelectItem key={`ext-a:${b.id}`} value={`ext:${b.id}`}>
                            {b.avatar} {b.name} ({b.rating} ELO) · внешний
                          </SelectItem>
                        ))}
                      </>
                    )}
                  </SelectContent>
                </Select>
                {selectedBotA && (() => {
                  const bot = bots.find(b => b.id === selectedBotA);
                  if (!bot) return null;
                  return (
                    <div className="text-sm text-muted-foreground bg-muted/30 rounded-md p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-lg">{bot.avatar}</span>
                        <Badge variant="outline" className="text-xs">{bot.style}</Badge>
                      </div>
                      <p className="text-xs">{bot.description}</p>
                    </div>
                  );
                })()}
              </div>

              <div className="space-y-3 p-4 rounded-lg border border-blue-500/20 bg-blue-500/5">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                  Бот B (Синий угол)
                </h3>
                <Select value={selectedBotB} onValueChange={setSelectedBotB}>
                  <SelectTrigger data-testid="select-bot-b">
                    <SelectValue placeholder="Выберите бота" />
                  </SelectTrigger>
                  <SelectContent>
                    {bots.map(b => (
                      <SelectItem key={b.id} value={b.id}>
                        {b.avatar} {b.name} ({b.rating} ELO)
                      </SelectItem>
                    ))}
                    {externalBots.length > 0 && (
                      <>
                        <div className="px-2 py-1.5 text-[10px] font-semibold text-muted-foreground uppercase">Внешние</div>
                        {externalBots.map(b => (
                          <SelectItem key={`ext-bot-b-${b.id}`} value={`ext:${b.id}`}>
                            {b.avatar} {b.name} ({b.rating} ELO) · внешний
                          </SelectItem>
                        ))}
                      </>
                    )}
                  </SelectContent>
                </Select>
                {selectedBotB && (() => {
                  const bot = bots.find(b => b.id === selectedBotB);
                  const ext = !bot ? externalBots.find(b => `ext:${b.id}` === selectedBotB) : null;
                  if (!bot && !ext) return null;
                  return (
                    <div className="text-sm text-muted-foreground bg-muted/30 rounded-md p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-lg">{bot?.avatar ?? ext?.avatar}</span>
                        {bot?.style && <Badge variant="outline" className="text-xs">{bot.style}</Badge>}
                        {ext && <Badge variant="outline" className="text-xs">Внешний webhook</Badge>}
                      </div>
                      <p className="text-xs">{bot?.description ?? "Отвечает через ваш callback URL."}</p>
                    </div>
                  );
                })()}
              </div>
            </div>

            {selectedBotA && selectedBotB && selectedBotA === selectedBotB && (
              <p className="text-xs text-destructive flex items-center gap-1">
                <Info className="w-3 h-3" /> Выберите разных ботов для дуэли
              </p>
            )}
          </Card>

          {/* Step 4: Mode + Judges */}
          <Card className={`p-5 space-y-4 transition-all ${
            !(selectedBotA && selectedBotB) ? "opacity-40 pointer-events-none" : ""
          }`}>
            <div className="flex items-center gap-3">
              <Scale className="w-5 h-5 text-amber-400" />
              <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Режим и судьи</h2>
            </div>

            {/* Mode selection */}
            <div className="flex gap-2">
              <Button variant={duelMode === "express" ? "default" : "outline"} size="sm" className="flex-1 text-xs"
                onClick={() => { setDuelMode("express"); setSelectedJudges(j => j.slice(0, 3)); }}>
                Экспресс (до 3 судей)
              </Button>
              <Button variant={duelMode === "classic" ? "default" : "outline"} size="sm" className="flex-1 text-xs"
                onClick={() => setDuelMode("classic")}>
                Классика со сменой сторон (до 5 судей)
              </Button>
            </div>

            {duelMode === "classic" && (
              <p className="text-xs text-muted-foreground bg-muted/30 p-2 rounded">
                Классика: поединок проводится дважды со сменой ролей. Судьи оценивают по итогам обоих раундов.
              </p>
            )}

            {/* Judge slots */}
            {(() => {
              const maxJudges = duelMode === "classic" ? 5 : 3;
              const judgableBots = bots.filter((b: any) => b.isJudge);
              const classicCategories = ["Направляющие на переговоры", "Устраивающиеся на работу", "Устраивающиеся на работу", "Устраивающиеся на работу", "Доверяющие собственность"];

              return (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Судьи ({selectedJudges.length}/{maxJudges})</span>
                    {selectedJudges.length < maxJudges && judgableBots.length > 0 && (
                      <Button variant="outline" size="sm" className="h-6 text-xs" onClick={() => setSelectedJudges([...selectedJudges, ""])}>
                        + Добавить
                      </Button>
                    )}
                  </div>
                  {selectedJudges.length === 0 && (
                    <p className="text-xs text-muted-foreground">Без судей — будет использован AI-судья по умолчанию (1 очко)</p>
                  )}
                  {selectedJudges.map((jId, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      {duelMode === "classic" && (
                        <span className="text-xs text-muted-foreground w-28 shrink-0 truncate">{classicCategories[idx] || `Судья ${idx + 1}`}</span>
                      )}
                      <Select value={jId} onValueChange={v => {
                        const updated = [...selectedJudges];
                        updated[idx] = v;
                        setSelectedJudges(updated);
                      }}>
                        <SelectTrigger className="h-8 text-xs">
                          <SelectValue placeholder="Выберите судью" />
                        </SelectTrigger>
                        <SelectContent>
                          {judgableBots.map(b => (
                            <SelectItem key={b.id} value={b.id}>{b.avatar} {b.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                        onClick={() => setSelectedJudges(selectedJudges.filter((_, i) => i !== idx))}>
                        ×
                      </Button>
                    </div>
                  ))}
                </div>
              );
            })()}
          </Card>

          <Button
            size="lg"
            className="w-full gap-2 font-semibold"
            disabled={!botVsBotReady}
            onClick={handleBotVsBot}
            data-testid="button-start-duel"
          >
            <Zap className="w-4 h-4" />
            Запустить поединок
          </Button>
        </TabsContent>

        {/* ─── Human vs Bot ─── */}
        <TabsContent value="human-vs-bot" className="space-y-4">
          <CaseRoleStepper
            selectedCaseId={selectedCaseHuman}
            onSelectCase={handleCaseSelectHuman}
            selectedRolePairId={selectedRolePairHuman}
            onSelectRolePair={setSelectedRolePairHuman}
            cases={cases}
            mode="human"
          />

          {/* Step 3: Opponent */}
          <Card className={`p-5 space-y-4 transition-all ${
            !selectedRolePairHuman ? "opacity-40 pointer-events-none" : ""
          }`}>
            <div className="flex items-center gap-3">
              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                selectedBotHuman ? "bg-primary text-primary-foreground" : selectedRolePairHuman ? "bg-muted text-muted-foreground" : "bg-muted/50 text-muted-foreground/50"
              }`}>
                {selectedBotHuman ? <Check className="w-3.5 h-3.5" /> : "3"}
              </span>
              <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Ваш оппонент</h2>
            </div>

            <Select value={selectedBotHuman} onValueChange={setSelectedBotHuman}>
              <SelectTrigger data-testid="select-bot-human">
                <SelectValue placeholder="Выберите бота-оппонента" />
              </SelectTrigger>
              <SelectContent>
                {bots.map(b => (
                  <SelectItem key={b.id} value={b.id}>
                    {b.avatar} {b.name} ({b.rating} ELO)
                  </SelectItem>
                ))}
                {externalBots.length > 0 && (
                  <>
                    <div className="px-2 py-1.5 text-[10px] font-semibold text-muted-foreground uppercase">Внешние</div>
                    {externalBots.map(b => (
                      <SelectItem key={`ext-human-${b.id}`} value={`ext:${b.id}`}>
                        {b.avatar} {b.name} ({b.rating} ELO) · внешний
                      </SelectItem>
                    ))}
                  </>
                )}
              </SelectContent>
            </Select>
          </Card>

          <Button
            size="lg"
            className="w-full gap-2 font-semibold"
            disabled={!humanVsBotReady || isLaunching}
            onClick={handleHumanVsBot}
            data-testid="button-start-human-duel"
          >
            <User className="w-4 h-4" />
            {isLaunching ? "Создаю поединок..." : "Начать тренировку"}
          </Button>
        </TabsContent>

        {/* ─── History ─── */}
        <TabsContent value="history" className="space-y-4" data-testid="tab-content-history">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-2">
              <History className="w-4 h-4" /> История поединков
            </h2>
            {duels.length > 0 && (
              <span className="text-xs text-muted-foreground">Всего: {duels.length}</span>
            )}
          </div>

          {duelsLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <Card key={i} className="p-4 animate-pulse">
                  <div className="h-5 bg-muted rounded w-2/3" />
                </Card>
              ))}
            </div>
          ) : sortedDuels.length === 0 ? (
            <Card className="p-8 text-center">
              <Swords className="w-10 h-10 mx-auto text-muted-foreground/40 mb-3" />
              <p className="text-sm text-muted-foreground">
                Поединков пока не было. Запустите первый на вкладке «Бот vs Бот» или «Я vs Бот».
              </p>
            </Card>
          ) : (() => {
            const totalHistoryPages = Math.max(1, Math.ceil(sortedDuels.length / HISTORY_PER_PAGE));
            const safePage = Math.min(historyPage, totalHistoryPages);
            const pagedDuels = sortedDuels.slice((safePage - 1) * HISTORY_PER_PAGE, safePage * HISTORY_PER_PAGE);
            return (
              <div className="space-y-2">
                {pagedDuels.map((duel) => (
                  <DuelHistoryItem key={duel.id} duel={duel} bots={bots} scenarios={scenarios} externalBots={externalBots} />
                ))}
                {totalHistoryPages > 1 && (
                  <div className="flex items-center justify-center gap-2 pt-3">
                    <Button variant="outline" size="sm" className="h-8 gap-1" disabled={safePage <= 1}
                      onClick={() => setHistoryPage(safePage - 1)}>
                      <ChevronLeft className="w-4 h-4" /> Назад
                    </Button>
                    <span className="text-xs text-muted-foreground">{safePage} / {totalHistoryPages}</span>
                    <Button variant="outline" size="sm" className="h-8 gap-1" disabled={safePage >= totalHistoryPages}
                      onClick={() => setHistoryPage(safePage + 1)}>
                      Далее <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            );
          })()}
        </TabsContent>
      </Tabs>
    </div>
  );
}
